/**
 * SKU code/name pattern tokens for Compose.
 *
 * Tokens: {SIZE} {SIZE_COMPACT}/{SIZENOX} {CODE}/{GROUP} {SERIES} {STYLE} {STYLE_ABBREV}
 */

export function sizeCompact(sizeLabel: string): string {
  return String(sizeLabel ?? "").replace(/[x×X]/g, "").trim();
}

/** First 2 alphanumeric chars, or explicit `Style=UT` / `Style|UT` suffix. */
export function styleAbbrev(styleKey: string): string {
  const raw = String(styleKey ?? "").trim();
  if (!raw) return "";
  const m = raw.match(/^(.+?)\s*[=|]\s*([A-Za-z0-9]{1,4})$/);
  if (m?.[2]) return m[2].toUpperCase();
  const letters = raw.replace(/[^A-Za-z0-9]/g, "");
  return letters.slice(0, 2).toUpperCase();
}

/** Display style key without `=UT` abbrev suffix. */
export function styleDisplayKey(styleKey: string): string {
  const raw = String(styleKey ?? "").trim();
  const m = raw.match(/^(.+?)\s*[=|]\s*[A-Za-z0-9]{1,4}$/);
  return (m?.[1] ?? raw).trim();
}

export type SkuPatternVars = {
  CODE: string;
  SIZE: string;
  STYLE: string;
  SERIES?: string;
};

export function applyNamingPattern(pattern: string, vars: SkuPatternVars): string {
  const code = vars.CODE ?? "";
  const series = vars.SERIES ?? code;
  const size = vars.SIZE ?? "";
  const style = styleDisplayKey(vars.STYLE ?? "");
  const compact = sizeCompact(size);
  const abbrev = styleAbbrev(vars.STYLE ?? "");
  return String(pattern ?? "")
    .replace(/\{SIZE_COMPACT\}/gi, compact)
    .replace(/\{SIZENOX\}/gi, compact)
    .replace(/\{STYLE_ABBREV\}/gi, abbrev)
    .replace(/\{GROUP\}/gi, code)
    .replace(/\{CODE\}/gi, code)
    .replace(/\{SERIES\}/gi, series)
    .replace(/\{SIZE\}/gi, size)
    .replace(/\{STYLE\}/gi, style)
    .replace(/-+/g, "-")
    .replace(/\s+/g, " ")
    .replace(/^-|-$/g, "")
    .trim();
}

export const DEFAULT_CODE_PATTERN = "{CODE}-{SIZE}-{STYLE}";
export const DEFAULT_NAME_PATTERN = "{SERIES} {SIZE} {STYLE}";
